import { useCallback, useEffect, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const FILTER_OPTIONS = [
  "FP",
  "EFP",
  "PMR",
  "UPGRADES",
  "SOLARLEADS",
  "SOLARCLOSED",
  "PERMITS",
  "INSTALLS",
  "RWS",
  "RWKS",
  "RWSC",
  "RWSI",
  "CONTACTS",
  "SIGNEDREPS",
  "RECRUITER",
  "SHPRO",
]

const TAG_OPTIONS = [
  "Rep",
  "Recruiter",
  "Team Lead",
  "Manager",
  "Senior Manager",
  "Regional",
  "Senior Regional",
  "Partner",
  "Division",
  "Office",
]

const TENURE_OPTIONS = ["All", "Rookie", "Veteran", "Sophomore"]

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
  formatValue,
}: {
  value: number
  maxVal: number
  colorClass?: string
  formatValue?: (v: number) => string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  const display = formatValue ? formatValue(value) : value.toLocaleString()
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <span className="text-sm tabular-nums shrink-0 text-ink w-12 text-right">{display}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function ChangeCell({ value }: { value: number }) {
  const isPositive = value >= 0
  const cls = isPositive ? "text-green-400" : "text-red-400"
  const prefix = isPositive ? "+" : ""
  return <span className={`text-sm tabular-nums font-medium ${cls}`}>{prefix}{Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%</span>
}

export const RanksReport = () => {
  const [date, setDate] = useState<string>("LASTWEEK")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [filter, setFilter] = useState<string>("FP")
  const [tag, setTag] = useState<string>("Senior Regional")
  const [groupBy, setGroupBy] = useState<string>("Senior Regional")
  const [tenure, setTenure] = useState<string>("All")
  const [userNames, setUserNames] = useState<string[]>([])
  const [usersFromServer, setUsersFromServer] = useState<{ name: string; badgeid: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<{
    success: boolean
    data: any[]
    sum?: number
    percentage?: number
    tags?: Record<string, string>
  } | null>(null)

  const userOptions = usersFromServer.map((u) => ({ value: u.name, label: u.name }))
  const selectedUsers = userOptions.filter((o) => userNames.includes(o.value))

  const fetchUsers = useCallback(() => {
    if (!tag) return
    service
      .ranksUsersByPosition(tag)
      .then((resp: any) => {
        const data = resp?.data?.data ?? resp?.data
        if (Array.isArray(data)) {
          setUsersFromServer(
            data.map((d: any) => ({ name: d.name ?? d.badgeid, badgeid: d.badgeid ?? d.name }))
          )
        } else {
          setUsersFromServer([])
        }
      })
      .catch(() => setUsersFromServer([]))
  }, [tag])

  const fetchReport = useCallback(() => {
    if (!date || !filter || !tag || !groupBy) return
    setLoading(true)
    const usersParam = userNames.length > 0 ? userNames.join(",") : "All"
    service
      .ranksFilter({ date, filter, tag, group_by: groupBy, tenure, users: usersParam })
      .then((resp: any) => {
        const payload = resp?.data ?? resp
        if (payload?.success !== false) {
          setResponse({
            success: true,
            data: payload?.data ?? [],
            sum: payload?.sum,
            percentage: payload?.percentage,
            tags: payload?.tags,
          })
        } else {
          setResponse(null)
        }
      })
      .catch(() => setResponse(null))
      .finally(() => setLoading(false))
  }, [date, filter, tag, groupBy, tenure, userNames])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    if (date && filter && tag && groupBy) fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const tableRows = response?.data ?? []
  const sum = response?.sum ?? 0
  const percentage = response?.percentage

  const maxTotal = tableRows.length ? Math.max(...tableRows.map((r: any) => Number(r.total) || 0), 1) : 1
  const maxPrevious = tableRows.length ? Math.max(...tableRows.map((r: any) => Number(r.previous) || 0), 1) : 1

  const totalPrevious = tableRows.reduce((s: number, r: any) => s + (Number(r.previous) || 0), 0)
  const totalTotal = tableRows.reduce((s: number, r: any) => s + (Number(r.total) || 0), 0)
  const totalPercentage = totalPrevious > 0 ? ((totalTotal - totalPrevious) / totalPrevious) * 100 : (totalTotal > 0 ? 100 : 0)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Ranks Report</h1>
        <p className="page-subtitle">
          Ranked metrics by position. Filters: date, metric, tag, group by, tenure, users.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value)
                } else {
                  setShowCustom(true)
                }
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-60 rounded-lg border border-line bg-white shadow-card-lg text-ink px-3 text-sm"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-60 py-2 px-3 text-sm"
                  />
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Metric (filter)</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {FILTER_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Tag (position)</label>
            <select
              value={tag}
              onChange={(e) => {
                setTag(e.target.value)
                setUserNames([])
              }}
              className="px-3 py-2 text-sm"
            >
              {TAG_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {TAG_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Tenure</label>
            <select
              value={tenure}
              onChange={(e) => setTenure(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {TENURE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[260px]">
            <label className="field-label">Users (optional; empty = all)</label>
            <Select
              isMulti
              isSearchable
              placeholder="All..."
              value={selectedUsers}
              options={userOptions}
              onChange={(selected) => setUserNames((selected || []).map((s) => s.value))}
              onMenuOpen={fetchUsers}
              className="react-select min-w-[240px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!date || !filter || !tag || !groupBy || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line flex flex-wrap items-center justify-between gap-2">
          <h2 className="card-title">
            {filter} by {groupBy}
          </h2>
          {(sum !== undefined && sum !== null) || percentage !== undefined ? (
            <div className="text-sm flex items-center gap-4">
              {sum !== undefined && sum !== null && <span className="text-ink-secondary">Total: {Number(sum).toLocaleString()}</span>}
              {percentage !== undefined && percentage !== null && (
                <span className={Number(percentage) >= 0 ? "text-green-400" : "text-red-400"}>
                  Total change: {Number(percentage) >= 0 ? "+" : ""}{Number(percentage).toLocaleString(undefined, { maximumFractionDigits: 1 })}%
                </span>
              )}
            </div>
          ) : null}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Full Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Total</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Previous</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Change %</th>
              </tr>
            </thead>
            <tbody>
              {loading && !response ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-ink-muted">
                    {response ? "No data for the selected filters." : "Select date, metric, tag and group by, then click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row: any, idx: number) => (
                  <tr key={row.full_name ?? row.name ?? row.title ?? idx} className="border-b border-line hover:bg-surface transition-colors">
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">
                      {row.full_name ?? row.name ?? row.title ?? "—"}
                    </td>
                    <td className="px-2 py-3">
                      <BarCell
                        value={Number(row.total) || 0}
                        maxVal={maxTotal}
                        colorClass="bg-blue-500"
                      />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell
                        value={Number(row.previous) || 0}
                        maxVal={maxPrevious}
                        colorClass="bg-gray-500"
                      />
                    </td>
                    <td className="px-2 py-3">
                      <ChangeCell value={Number(row.change) ?? 0} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {tableRows.length > 0 && (
              <tfoot>
                <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                  <td className="px-2 py-3 whitespace-nowrap">Total</td>
                  <td className="px-2 py-3">{totalTotal.toLocaleString()}</td>
                  <td className="px-2 py-3">{totalPrevious.toLocaleString()}</td>
                  <td className="px-2 py-3">
                    <span className={totalPercentage >= 0 ? "text-green-400" : "text-red-400"}>
                      {totalPercentage >= 0 ? "+" : ""}{Number(totalPercentage).toLocaleString(undefined, { maximumFractionDigits: 1 })}%
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
